import { ArrowSmRightIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import {
  Anchor,
  Form,
  Paragraph,
  StickyFooter,
  TextareaField,
} from 'src/components'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import InputField from 'src/components/Shared/UIComponents/InputField'
import { getBackendByRegion } from 'src/regions'
import { savedLoginUrl, savedPrincipalArn, savedRoleArn } from '../../storage'
import { useEditOrganization } from 'src/routes/Onboarding/queries'
import { useResyncSubscriptionProvisioning } from '../../queries'
import { savedTokens } from 'src/auth'
import { toast } from 'sonner'
import { permissionPolicy } from '../utils'
import TooltipLabel from 'src/components/Shared/UIComponents/TooltipLabel'

type Props = {
  organization: string
  username: string
  connectUrl: string
  instanceId: string
  accountId: string
  region: string
  arn: string
}

export const AWSIAMContent = (props: Props) => {
  const navigate = useNavigate()
  const editOrganization = useEditOrganization()
  const resyncSubscription = useResyncSubscriptionProvisioning()

  const [loginUrl, setLoginUrl] = savedLoginUrl.useState('')
  const [principalArn, setPrincipalArn] = savedPrincipalArn.useState('')
  const [roleArn, setRoleArn] = savedRoleArn.useState('')

  const link = new URL(
    `/token/sso?org=${props.organization}`,
    getBackendByRegion(savedTokens.get()?.region),
  ).href

  return (
    <div className="flex flex-col gap-6 mt-8 text-secondary">
      <div className="flex flex-col gap-2">
        <Paragraph className="font-bold underline">Step 1</Paragraph>
        <Paragraph>
          To connect with AWS Identity and Access Management (IAM), follow the
          exact same steps as in this{' '}
          <Anchor
            href="https://catalog.us-east-1.prod.workshops.aws/workshops/33e6d0e7-f927-4531-abb1-f28a86ba0872/en-US/6-sso-amazonconnect-aws-iam-identity-center"
            target="_blank"
            className="underline"
          >
            installation guide
          </Anchor>
          .
        </Paragraph>
      </div>

      <div className="flex flex-col gap-2">
        <Paragraph className="font-bold underline">Step 2</Paragraph>
        <Paragraph>
          During step 1, you have created the following resources in AWS IAM:
        </Paragraph>
        <ol className="list-decimal pl-5 mb-4">
          <li>IAM Identity Provider</li>
          <li>IAM Role</li>
        </ol>
        <Paragraph>
          a) Please enter the ARN of the these resources below
        </Paragraph>
      </div>

      <Form
        className="gap-12"
        onSubmit={async (e) => {
          e.preventDefault()
          if (
            loginUrl &&
            principalArn &&
            props.region &&
            roleArn &&
            props.connectUrl &&
            props.instanceId &&
            props.accountId &&
            props.arn
          ) {
            await editOrganization.mutateAsync({
              cognito_url: '',
              userPoolAppClientId: '',
              userPoolClientSecret: '',
              userPoolId: '',
              userPoolRegion: '',
              userPoolTokenType: '',
              aws_connect_saml_url: props.connectUrl,
              iam_organization_admin_accessKey: '',
              iam_organization_admin_region: '', //didn't exist
              iam_organization_usergroup: '', //didn't exist
              redirect_uri: '', //didn't exist
              aws_connect_type: 'SSO',
              iam_organization_admin_accessSecret: '',
              iam_organization_policy_arn: '',
              code: props.organization,
              organization_connect_account_id: props.accountId,
              organization_connect_instance_id: props.instanceId,
              organization_connect_arn: props.arn,
              setupStep: 'finished',
              username: props.username,
              organization: props.organization,
              region: props.region,
              sso: {
                loginUrl: loginUrl,
                principalArn: principalArn,
                region: props.region,
                roleArn: roleArn,
                type: 'awsiam',
              },
              application: {
                chatregion: props.region,
              },
            })

            await resyncSubscription.mutateAsync({
              organization: props.organization,
            })

            navigate('../finish')
          } else {
            toast.error(`ARN is not valid.`)
          }
        }}
      >
        <div className="col-span-full grid gap-10">
          <div className="col-span-full grid gap-4">
            <InputField
              label="Identity provider ARN"
              placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
              value={principalArn}
              setValue={(value) => {
                setPrincipalArn(value.trim())
              }}
              title="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
              pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
              className="col-span-full"
              tooltip={
                <p>
                  Navigate to{' '}
                  <b>
                    AWS Management console -&gt; IAM -&gt; Identity Providers
                    -&gt; IdentityCenter-SSO -&gt; ARN
                  </b>
                </p>
              }
            />
            <InputField
              label="Role ARN"
              placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
              value={roleArn}
              setValue={(value) => {
                setRoleArn(value.trim())
              }}
              title="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
              pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
              className="col-span-full"
              tooltip={
                <p>
                  Navigate to{' '}
                  <b>
                    AWS Management console -&gt; IAM -&gt; Roles -&gt; Click
                    “IdentityCenter_SAML_Role” role -&gt; ARN
                  </b>
                </p>
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <TooltipLabel
              label={
                <Paragraph className="font-normal text-[16px]">
                  b) Please change the condition in your permission policy to
                  the below:
                </Paragraph>
              }
              tooltip={
                <div className="flex flex-col gap-4">
                  <Paragraph className="font-bold">
                    AWS Management Console -&gt; IAM &gt; Roles &gt; ROLE_NAME
                  </Paragraph>
                  <img
                    alt="Permission policy"
                    width={600}
                    src={require('src/images/permission_policy.png')}
                  />
                </div>
              }
            />
            <div className="flex flex-row gap-4 items-center">
              <TextareaField
                classNameInput="h-32 text-sm overflow-hidden"
                className="w-1/2"
                value={permissionPolicy}
                disabled
              />
              <CustomButton
                type="button"
                label="copy"
                onClick={() => {
                  navigator.clipboard.writeText(permissionPolicy)
                  toast.success(
                    `Permission policy has been copied to your clipboard.`,
                  )
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Paragraph className="font-bold underline">Step 3</Paragraph>
            <div className="flex flex-col gap-4">
              <Paragraph>
                During step 1, you created an application in AWS Identity
                center, called 'Amazon Connect Agent'. You should adjust the
                application startup information in this application. More
                specifically, change the "<b>Application ACS URL</b>" to the
                below URL so that it points to Mobile Connect.
              </Paragraph>
              <Paragraph>
                Navigate to{' '}
                <b>
                  AWS Management console -&gt; IAM Identity center -&gt;
                  Applications-&gt; Customer managed -&gt; Select Application
                  -&gt; Actions -&gt; Edit configuration -&gt; Application Start
                  URL
                </b>{' '}
                and paste the below URL.
              </Paragraph>
              <div className="flex items-end col-span-full gap-4">
                <InputField
                  label="Application ACS URL"
                  placeholder=""
                  value={link}
                  disabled
                  setValue={() => {}}
                  className="bg-foreground text-secondary"
                />
                <CustomButton
                  type="button"
                  label="copy"
                  onClick={() => {
                    navigator.clipboard.writeText(link)
                    toast.success(
                      `Setup link has been copied to your clipboard.`,
                    )
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Paragraph className="font-bold underline">Step 4</Paragraph>
            <div className="flex flex-col gap-4">
              <Paragraph>
                During step 1, you created an application in AWS Identity
                center, called 'Amazon Connect Agent'. As a last step, we need
                the SSO login URL. To find this, navigate to{' '}
                <b>
                  AWS Management console -&gt; IAM Identity center -&gt;
                  Settings summary
                </b>
                . open the AWS access portal URL. After opening the portal URL,
                right click the Mobile Connect application and{' '}
                <b>copy link address</b>. Paste this URL below.
              </Paragraph>

              <InputField
                label="SSO Login Url"
                placeholder="https://#ID.awsapps.com/start/#/saml/default/Mobile%20Connect/ins-#ID"
                value={loginUrl}
                setValue={(value) => {
                  setLoginUrl(value.trim())
                }}
                type="url"
                className="col-span-full"
                tooltip={
                  <img
                    src={require('src/images/awsiam_login.png')}
                    width="600"
                    alt="AWS IAM where to get login url - "
                  />
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Paragraph className="font-bold underline">Step 5</Paragraph>
            <Paragraph>
              As a last step, please make sure that the you assign the newly
              created role in AWS IAM identity center to the users that you
              would like to give access to Mobile Connect.
            </Paragraph>
          </div>
        </div>
        <StickyFooter>
          <EmailHelpBox className="flex-1" />
          <CustomButton
            type="submit"
            label="next"
            isLoading={
              editOrganization.isLoading || resyncSubscription.isLoading
            }
            icon={<ArrowSmRightIcon />}
            iconPosition="right"
          />
        </StickyFooter>
      </Form>
    </div>
  )
}
